import React from 'react';
import { Box, Button, CircularProgress, Skeleton, Stack, Typography } from '@mui/material';

// Loading and empty are states the user spends real time looking at, and
// they were the least designed part of the app: three different spinner
// paddings (p:4, py:6, py:10), and empty lists that variously said nothing,
// said "No users found" inside a table cell, or said "Nothing running right
// now." as bare grey text with no way to act on it.

// ---------------------------------------------------------------- Loading

/** A centred spinner, at one height everywhere. Use for whole-screen waits. */
export const LoadingState: React.FC<{ label?: string; height?: number }> = ({
  label, height = 280,
}) => (
  <Stack alignItems="center" justifyContent="center" gap={1.5} sx={{ height }}>
    <CircularProgress size={28} />
    {label && (
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    )}
  </Stack>
);

/**
 * Rows that keep the page's shape while it loads. Preferred over a spinner
 * for tables: the layout does not jump when the data lands.
 */
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 6, columns = 5,
}) => (
  <Box sx={{ p: 2 }}>
    {Array.from({ length: rows }).map((_, r) => (
      <Stack key={r} direction="row" gap={2} sx={{ py: 1.25 }}>
        {Array.from({ length: columns }).map((__, c) => (
          <Skeleton
            key={c}
            variant="text"
            sx={{ flex: c === 0 ? 2 : 1, fontSize: '0.9375rem' }}
          />
        ))}
      </Stack>
    ))}
  </Box>
);

// ------------------------------------------------------------------ Empty

/**
 * An empty state says what would be here, why it is not, and — when there
 * is one — offers the action that fills it. "No users found" does none of
 * those three.
 */
export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  action?: { label: string; onClick: () => void; icon?: React.ReactNode };
  compact?: boolean;
}> = ({ icon, title, description, action, compact }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    gap={1}
    sx={{ py: compact ? 5 : 8, px: 3, textAlign: 'center' }}
  >
    {icon && (
      <Box
        sx={{
          width: 44, height: 44, borderRadius: '50%',
          display: 'grid', placeItems: 'center',
          bgcolor: 'grey.100', color: 'text.disabled', mb: 0.5,
          '& svg': { fontSize: 22 },
        }}
      >
        {icon}
      </Box>
    )}
    <Typography variant="h5" color="text.primary">{title}</Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>
        {description}
      </Typography>
    )}
    {action && (
      <Button
        variant="outlined"
        startIcon={action.icon}
        onClick={action.onClick}
        sx={{ mt: 1.5 }}
      >
        {action.label}
      </Button>
    )}
  </Stack>
);
